import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { sessionResponseSchema, type SessionResponse } from '@unibyte/contracts';
import { apiRequest, setAccessToken, setSessionExpiredHandler } from './api.js';

type AuthUser = SessionResponse['user'];

interface SessionState {
  user: AuthUser | null;
  ready: boolean;
  login: (email: string, password: string) => Promise<string | null>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<string | null>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

async function readSession(response: Response): Promise<AuthUser | null> {
  const body: unknown = await response.json().catch(() => null);
  const parsed = sessionResponseSchema.safeParse(body);
  if (!response.ok || !parsed.success) return null;
  setAccessToken(parsed.data.accessToken);
  return parsed.data.user;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      setAccessToken(null);
      setUser(null);
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  useEffect(() => {
    let active = true;
    void apiRequest('/v1/auth/refresh', { method: 'POST' }, false).then(async (response) => {
      const next = response.ok ? await readSession(response) : null;
      if (!active) return;
      setUser(next);
      setReady(true);
    });
    return () => {
      active = false;
    };
  }, []);

  async function login(email: string, password: string): Promise<string | null> {
    const response = await apiRequest(
      '/v1/auth/login',
      { method: 'POST', body: JSON.stringify({ email, password }) },
      false,
    );
    const next = await readSession(response);
    if (!next) return 'INVALID_CREDENTIALS';
    setUser(next);
    return null;
  }

  async function changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<string | null> {
    const response = await apiRequest('/v1/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    const next = await readSession(response);
    if (!next) return 'CHANGE_PASSWORD_FAILED';
    setUser(next);
    return null;
  }

  async function logout(): Promise<void> {
    await apiRequest('/v1/auth/logout', { method: 'POST' }, false);
    setAccessToken(null);
    setUser(null);
  }

  return (
    <SessionContext.Provider value={{ user, ready, login, changePassword, logout }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside SessionProvider');
  return value;
}
